import { isEqual } from 'lodash';

import type { SkillAttachmentChanges } from './skill-attachment-editor';

/**
 * The attachment changes still to save once a save has landed.
 *
 * The author can keep editing while a save is in flight (the document picker's "Update
 * copy" resolves asynchronously, for one). Whatever they changed relative to the snapshot
 * that was submitted is carried forward and rebased on the version the save produced:
 * new or edited files, renames, and removals, with paths the save renamed followed to
 * their new place.
 */
export function getRemainingSkillAttachmentChanges({
  current,
  previousPaths,
  savedAttachments,
  submitted,
}: {
  /** The changes as they stand now. */
  current: SkillAttachmentChanges;
  /** Attachment paths of the version the save was based on. */
  previousPaths: string[];
  /** Attachment paths of the version the save produced. */
  savedAttachments: { path: string; sourceDocumentId?: string | null }[];
  /** The changes the save sent. */
  submitted: SkillAttachmentChanges;
}): SkillAttachmentChanges {
  if (isEqual(current, submitted)) return {};
  const savedPaths = savedAttachments.map(file => file.path);

  // Where a file saved before this save lives now.
  const getSavedPath = (path: string) =>
    submitted.attachmentRenames?.find(rename => rename.path === path)?.newPath ?? path;
  const removedPaths = new Set(
    current.retainedAttachmentPaths
      ? (submitted.retainedAttachmentPaths ?? previousPaths)
          .filter(path => !current.retainedAttachmentPaths?.includes(path))
          .map(getSavedPath)
      : []
  );

  const textFiles = getUnsubmitted(current.textFiles, submitted.textFiles);
  const pdfAttachments = getUnsubmitted(current.pdfAttachments, submitted.pdfAttachments);
  const documentSnapshots = getUnsubmitted(current.documentSnapshots, submitted.documentSnapshots);
  // A failed attempt can commit files the author has since removed or replaced in the draft.
  for (const file of submitted.textFiles ?? [])
    if (!current.textFiles?.some(next => next.path === file.path)) removedPaths.add(file.path);
  for (const file of submitted.pdfAttachments ?? [])
    if (!current.pdfAttachments?.some(next => isEqual(next, file)))
      for (const saved of savedAttachments)
        if (saved.path.split('/').at(-1) === `${file.id}.pdf`) removedPaths.add(saved.path);
  for (const file of submitted.documentSnapshots ?? [])
    if (!current.documentSnapshots?.some(next => isEqual(next, file)))
      for (const saved of savedAttachments)
        if (saved.sourceDocumentId === file.documentId) removedPaths.add(saved.path);

  const attachmentRenames = getUnsubmitted(current.attachmentRenames, submitted.attachmentRenames)
    ?.map(rename => ({ ...rename, path: getSavedPath(rename.path) }))
    .filter(rename => savedPaths.includes(rename.path) && !removedPaths.has(rename.path));
  const retainedAttachmentPaths = savedPaths.filter(
    path => !removedPaths.has(path) && !textFiles?.some(file => file.path === path)
  );

  return {
    ...(textFiles?.length ? { textFiles } : {}),
    ...(pdfAttachments?.length ? { pdfAttachments } : {}),
    ...(documentSnapshots?.length ? { documentSnapshots } : {}),
    ...(attachmentRenames?.length ? { attachmentRenames } : {}),
    ...(retainedAttachmentPaths.length !== savedPaths.length ? { retainedAttachmentPaths } : {}),
  };
}

/**
 * Whether the pending changes would change the skill's files. A list of retained paths that
 * still names every saved file keeps them all, so on its own it is not a change.
 */
export function getHasSkillAttachmentChanges(
  changes: SkillAttachmentChanges,
  savedPaths: string[]
): boolean {
  return Boolean(
    changes.textFiles?.length ||
    changes.pdfAttachments?.length ||
    changes.documentSnapshots?.length ||
    changes.attachmentRenames?.length ||
    (changes.retainedAttachmentPaths &&
      savedPaths.some(path => !changes.retainedAttachmentPaths?.includes(path)))
  );
}

/** The entries of `current` that the save did not send as they are. */
function getUnsubmitted<T>(
  current: T[] | null | undefined,
  submitted: T[] | null | undefined
): T[] | undefined {
  return current?.filter(item => !submitted?.some(saved => isEqual(saved, item)));
}
